// Shared escrow-outcome copy for Trade Offers. Used by the offer builder footer,
// the offer review screen, and (later) the trade suggestions surface, so the three
// surfaces always state the same outcome for the same pair of balances.
//
// Inputs are explicit booleans by ruling: the backend's funding enum names who can
// FUND, while this copy states who SHIPS FIRST, and those are opposite parties.
// The mapping lives here, once, with a test — a leg ships first when its SENDER's
// counterpart cannot fund their own inbound escrow.

export type FundingOutcome =
  | 'both_ship_now'
  | 'viewer_ships_first'
  | 'counterparty_ships_first'
  | 'neither_funded_yet';

export interface FundingCopyInput {
  /** Viewer's points cover the total of the cards coming TO the viewer */
  viewerCanFund: boolean;
  /** Counterparty's points cover the total of the cards coming TO them */
  counterpartyCanFund: boolean;
  counterpartyUsername: string;
}

/** A side with nothing inbound has nothing to fund */
export const canFundInbound = (points: number, inboundTotalPoints: number): boolean =>
  inboundTotalPoints <= 0 || points >= inboundTotalPoints;

export const getFundingOutcome = (
  viewerCanFund: boolean,
  counterpartyCanFund: boolean
): FundingOutcome => {
  if (viewerCanFund && counterpartyCanFund) return 'both_ship_now';
  if (!viewerCanFund && counterpartyCanFund) return 'viewer_ships_first';
  if (viewerCanFund && !counterpartyCanFund) return 'counterparty_ships_first';
  return 'neither_funded_yet';
};

// The three live strings are verbatim from the design-pass ticket + Eric's
// ruling (2026-07-28). neither_funded_yet is structural only — the
// block-unfundable-offers ticket owns that state's final behavior.
export const getFundingMessage = ({
  viewerCanFund,
  counterpartyCanFund,
}: FundingCopyInput): string => {
  switch (getFundingOutcome(viewerCanFund, counterpartyCanFund)) {
    case 'both_ship_now':
      return `You can both cover the points for the cards you're receiving, so both sides ship right away.`;
    case 'viewer_ships_first':
      return `Your cards ship first. Their cards send once yours arrive and pay you.`;
    case 'counterparty_ships_first':
      return `Their cards ship first. Yours send once theirs arrive and pay them.`;
    case 'neither_funded_yet':
      // The old second sentence ("Only one of you has to cover it for this
      // trade to go through") is deliberately gone. It is false whenever both
      // sides must absorb their own difference, and it shipped as a bug on the
      // accept path by telling users a single top-up would unlock the trade.
      // The builder states the real shortfalls instead (getCoverageShortfallLine);
      // this string stays as the outcome-only form for surfaces with no
      // shortfall numbers to hand, e.g. OfferSummaryCard.
      return `Neither of you has enough points yet to receive the other's cards.`;
  }
};

/**
 * The builder's unfunded line, naming each party's REAL requirement rather than
 * their gross arriving value. Whoever moves second is paid by the first leg and
 * only needs the difference, so a gross number over-states their shortfall by
 * the whole value of the first leg — the exact defect this copy replaces.
 *
 * A zero shortfall means that party has nothing to cover, so its clause is
 * OMITTED rather than printed as zero: naming only the other party would imply
 * their top-up alone unlocks the trade, which is the banned claim.
 */
export const getCoverageShortfallLine = ({
  uncoveredCount,
  viewerShortfall,
  counterpartyShortfall,
}: {
  uncoveredCount: number;
  viewerShortfall: number;
  counterpartyShortfall: number;
}): string => {
  const cards = `${uncoveredCount.toLocaleString()} ${uncoveredCount === 1 ? 'card' : 'cards'}`;
  const lead = `${cards} ${uncoveredCount === 1 ? "isn't" : "aren't"} covered`;

  const clauses: string[] = [];
  if (viewerShortfall > 0) clauses.push(`you're ${viewerShortfall.toLocaleString()} short`);
  if (counterpartyShortfall > 0) {
    clauses.push(`they're ${counterpartyShortfall.toLocaleString()} short`);
  }

  if (clauses.length === 0) return `${lead}.`;
  return `${lead}, ${clauses.join(' and ')}.`;
};

// Tooltip behind the coverage divider. Paragraph two is NOT optional: without
// it the tooltip explains only the viewer-moves-first case, and a user whose
// budget includes incoming value has no way to understand the number on the
// divider.
export const OFFER_COVERAGE_TOOLTIP_ESCROW =
  "Trade size is limited by both partners' point balances. Whoever moves first has to cover the full value of the cards arriving to them, so at least one of you needs enough points for that first leg. You get all of it back as the other side's cards land.";

export const OFFER_COVERAGE_TOOLTIP_SECOND_LEG =
  'Whoever goes second only needs the difference between the two sides, because the first leg has already paid them.';

export const OFFER_COVERAGE_TOOLTIP_ORDERING =
  'Points cover the most valuable cards first, so a shortfall always lands on the smallest one.';

/**
 * Ship-order copy from the suggestion payload's `shipsFirst`, which states the
 * ship order DIRECTLY.
 *
 * This exists so the suggestion surface never routes through getFundingMessage:
 * that function takes who CAN FUND, which is the OPPOSITE party from who ships
 * first, and every consumer that re-derived one from the other got it backwards.
 * `shipsFirst` has exactly three values (the SuggestionFundsSide enum), so the
 * switch is exhaustive and getFundingMessage's fourth outcome (neither_funded_yet)
 * is correctly unreachable here — the engine only generates fundable bundles.
 *
 * The three strings are the SAME ones getFundingMessage returns, so the offer
 * surfaces and the suggestion surface state the same outcome in the same words.
 */
export const getShipsFirstMessage = (
  shipsFirst: 'BOTH' | 'VIEWER' | 'COUNTERPARTY'
): string => {
  switch (shipsFirst) {
    case 'BOTH':
      return getFundingMessage({
        viewerCanFund: true,
        counterpartyCanFund: true,
        counterpartyUsername: '',
      });
    case 'VIEWER':
      // The viewer ships first, i.e. the viewer is the side that cannot fund.
      return getFundingMessage({
        viewerCanFund: false,
        counterpartyCanFund: true,
        counterpartyUsername: '',
      });
    case 'COUNTERPARTY':
      return getFundingMessage({
        viewerCanFund: true,
        counterpartyCanFund: false,
        counterpartyUsername: '',
      });
  }
};

export interface NetCopy {
  /** "You receive 1,240 pts" / "You pay 650 pts" / the even line */
  netLine: string;
  /** Plain-language reason points flow opposite to cards; null on an even trade */
  whyLine: string | null;
  /** Which way points flow, for surfaces composing words + the branded Points pill */
  netDirection: 'receive' | 'pay' | 'even';
  /** Absolute net amount; null on an even trade (render netLine instead) */
  netAmount: number | null;
  /** The words before the branded Points pill: 'You receive' / 'You pay' / '' when even */
  netPrefix: string;
}

/**
 * The net stated in words from the viewer, with its why line. Points flow
 * toward the side sending more card value: each leg settles independently, so
 * the viewer pays escrow for what they receive and earns points for what they
 * send. Used by the offer card, the builder commit bar, and the review screen.
 * Surfaces render netPrefix + <Points points={netAmount}> (the branded coin
 * pill per /branding); netLine stays the authoritative plain-string form.
 */
export const getNetCopy = (youSendTotal: number, theySendTotal: number): NetCopy => {
  const net = youSendTotal - theySendTotal;
  if (net > 0) {
    return {
      netLine: `You receive ${net.toLocaleString()} pts`,
      whyLine: 'Card value leaving is greater, so points come back to you.',
      netDirection: 'receive',
      netAmount: net,
      netPrefix: 'You receive',
    };
  }
  if (net < 0) {
    return {
      netLine: `You pay ${Math.abs(net).toLocaleString()} pts`,
      whyLine: 'Card value arriving is greater, so points leave your balance.',
      netDirection: 'pay',
      netAmount: Math.abs(net),
      netPrefix: 'You pay',
    };
  }
  return {
    netLine: 'Even trade. No points change hands.',
    whyLine: null,
    netDirection: 'even',
    netAmount: null,
    netPrefix: '',
  };
};

/** Review-screen reassurance shown under either ships-first escrow outcome */
export const OFFER_SEQUENCED_REASSURANCE =
  'Nothing else to do, the second leg opens by itself.';

// The price-lock notice appears in the builder footer and again on the review
// screen directly above Accept; the not-held notice sets expectations that a
// failed accept is a normal outcome, since nothing is reserved while pending.
export const OFFER_PRICE_LOCK_NOTICE =
  'Prices lock when the offer is proposed and stay locked for the life of this trade.';

export const OFFER_CARDS_NOT_HELD_NOTICE =
  "Cards aren't held while an offer is pending, so if one is traded away first, the offer is cancelled and no trade is created.";
